import React, { useState, useEffect, useContext, useCallback } from "react";
import { Button, Table, Space, Dropdown, notification, Input, Tooltip, Select, Modal } from "antd";
import { DeleteOutlined, EditOutlined, DownOutlined, SearchOutlined, UnorderedListOutlined, PlayCircleOutlined } from "@ant-design/icons";
import MeetingForm from "../components/Forms/MeetingForm";
import axios from "axios";
import GlobalContext from "../utils/GlobalContext";
import { v4 as uuidv4 } from "uuid";
import DeleteMeetingDialog from "../components/DeleteMeetingDialog";
import LoadingModal from "../utils/LoadingModal";
import formatTimestamp from "../utils/FormatTimestamp";
import { Link } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { MEETING_STATUS } from "../utils/Constants";

const PAGE_SIZE = 15;
const { Option } = Select;

const MeetingTable = () => {
  const navigate = useNavigate();
  const globals = useContext(GlobalContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentMeeting, setCurrentMeeting] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
  });
  const [messageVisible, setMessageVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [allstatus, setAllStatus] = useState([]);
  const [statusId, setStatusId] = useState("");
  // const userRole = globals.userRole;
  const userEmail = globals.userEmail;

  const columns = [
    {
      title: "Fecha",
      dataIndex: "startDate",
      key: "startDate",
      render: (_, record) => formatTimestamp(record.startDate),
    },
    {
      title: "Nombre de la reunión",
      dataIndex: "title_meeting",
      key: "title_meeting",
      render: (_, record) => {
        //navigate to template detail
        return (
          (record.statusId === MEETING_STATUS.EN_EJECUCION || record.statusId === MEETING_STATUS.FINALIZADA) ? <Link to={`/meeting/${record.id}/execute`}>{record.title_meeting}</Link> : <Link to={`/meeting/${record.id}`}>{record.title_meeting}</Link>
        )
      }
    },
    {
      title: "Tipo de reunión",
      dataIndex: "meeting_type_name",
      key: "meeting_type_name",
    },
    {
      title: "Departamento",
      dataIndex: "department_name",
      key: "department",
    },
    {
      title: "Estado",
      dataIndex: "status_name",
      key: "status",
      render: (_, record) => {
        if (record.status_name === undefined){
          return (
            <div>{record?.status?.name}</div>
          )
        }
        else{
          return (
            <div>{record.status_name}</div>
          )
        }
      }
    },
    {
      title: "Invitados",
      dataIndex: "meeting_guests",
      key: "guests",
      render: (_, record) => {
        const guestList = Array.isArray(record?.meeting_guests)
        ? record.meeting_guests.map(guest => guest.email.trim())
        : record.meeting_guests.split(',').map(guest => guest.trim());
        if (guestList === null || guestList === undefined || guestList.length === 0 || guestList === "") {
          return (
            <Button type="link">Sin invitados</Button>
          )
        }
        const g = guestList.map((guest, index) => {
          return <div key={index}>{guest}</div>
        })
        if (guestList.length === 1) {
          if (guestList[0] === "") {
            return (
              <Button type="link">Sin invitados</Button>
            )
          }
        }
        return (
        <Tooltip title={g}>
          <Button type="link">{`${guestList.length} `}{(guestList.length > 1) ? `personas` : `persona`}</Button>
        </Tooltip>
        )
      },
    },
    {
      title: "Anfitrión",
      dataIndex: "creator",
      key: "creator",
      width: 250,
    },
    {
      title: "Opciones",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <Dropdown
            trigger={["click"]}
            menu={{
              items:[
                {
                  key: '0',
                  label: (record.statusId === MEETING_STATUS.ABIERTA) ? 'Ejecutar reunión' : 'Ver Reunión',
                  icon:<PlayCircleOutlined />,
                  onClick: () => handleActionClick((record.statusId === MEETING_STATUS.ABIERTA) ? "execute" : "view", record)
                },
                ...(record.statusId === MEETING_STATUS.ABIERTA ? [{
                  key: '1',
                  label: 'Ver puntos',
                  icon:<UnorderedListOutlined />,
                  onClick: () => handleActionClick("points", record)
                },] : []),
                {
                  key: '2',
                  label: 'Editar',
                  icon:<EditOutlined />,
                  onClick: () => handleActionClick("edit", record)
                },
                {
                  key: '3',
                  label: 'Cancelar',
                  icon:<DeleteOutlined />,
                  onClick: () => handleActionClick("delete", record)
                },
              ],
            }}
          >
            <Button icon={<DownOutlined />} shape="circle" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const fetchData = useCallback(
    async (newPagination, search, status_id) => {
      let page = 1;
      let pageSize = PAGE_SIZE;
      if (newPagination) {
        page = newPagination.current;
        pageSize = newPagination.pageSize;
      }
      try {
        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 100));

        let params = {
            page,
            limit: pageSize,
            search: search,
            statusId: status_id,
            user: userEmail,
          }
        // if (userRole.toLowerCase() === "user") {
        //   params.user = userEmail;
        // }
        const response = await axios.get(
          `${globals.SERVICE_API_DB}/v1/list-meeting`,
          {
            headers: {
              "x-token": globals.token,
            },
            params,
          }
        );
        if (response.data.status !== 0){
          setLoading(false);
          return;
        }
        setData(response?.data?.data?.rows);
        if (newPagination) {
          setPagination({
            ...newPagination,
            total: response?.data?.data?.count,
          });
        } else {
          setPagination({
            ...pagination,
            total: response?.data?.data?.count,
          }
          );
        }
        notification.success({
          message: "Información obtenida exitosamente!",
          placement: "bottomRight",
          duration: 2,
        });
      } catch (error) {
        console.log("Error fetching data:", error.response);
        if (error?.response?.data?.status !== -3) {
          console.error("Error fetching data:", error.response);
          notification.error({
            message: "Error obteniendo información!",
            placement: "bottomRight",
            duration: 2,
          });
        }
        else{
          notification.warning({
            message: "No hay reuniones disponibles!",
            placement: "bottomRight",
            duration: 2,
          });
        
        }
      }
      setLoading(false);
    },
    // eslint-disable-next-line
    [globals.SERVICE_SERVICE_API_DB]
  );

  const fetchStatus = useCallback(async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const response = await axios.get(
        `${globals.SERVICE_API_DB}/v1/catalogue-status`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setAllStatus(response?.data?.data);
    } catch (error) {
      console.error("Error fetching status:", error.response);
      notification.error({
        message: "Error obteniendo estados de reunión",
        placement: "bottomRight",
      });
    }
    // setLoadingClients(false);
  }, [globals.SERVICE_API_DB, globals.token]);

  useEffect(() => {
    fetchData(null, null);
    fetchStatus();
  }, [fetchData, fetchStatus]);

  const handleActionClick = async (key, record) => {
    const isOwner = record.creator === userEmail;
    const isCohost = record.co_host === userEmail;
    const isAdmin = globals.userRole === "admin";
    if (key === "edit") {
      if (record.statusId === MEETING_STATUS.FINALIZADA || record.statusId === MEETING_STATUS.CANCELADA){
        notification.warning({
          message: "No puedes editar reuniones cerradas o canceladas",
          placement: "bottomRight",
          duration: 2,
        });
        return;
      }

      setIsEdit(true);
      setCurrentMeeting(record);
      setShowMeetingForm(true);
    }
    else if (key === "delete") {

      if (!isOwner && !isAdmin) {
        notification.warning({
          message: "No puedes cancelar reuniones que no has creado",
          placement: "bottomRight",
          duration: 4,
        });
        return;
      }

      if (record.statusId === MEETING_STATUS.FINALIZADA || record.statusId === MEETING_STATUS.CANCELADA){
        notification.warning({
          message: "No puedes cancelar reuniones cerradas o canceladas",
          placement: "bottomRight",
          duration: 2,
        });
        return;
      }
      
      setCurrentMeeting(record);
      setMessageVisible(true);
    }
    else if (key === "points") {
      navigate(`/meeting/${record.id}`);
    }
    else if (key === "execute"){

      if (!isOwner && !isCohost && !isAdmin) {
        notification.warning({
          message: "No puedes ejecutar reuniones que no has creado",
          placement: "bottomRight",
          duration: 4,
        });
        return;
      }

      if (record.statusId === MEETING_STATUS.CANCELADA){
        notification.warning({
          message: "No puedes ejecutar una reunión finalizada o cancelada",
          placement: "bottomRight",
          duration: 2,
        });
        return;
      }
      if (record.statusId === MEETING_STATUS.EN_EJECUCION || record.statusId === MEETING_STATUS.FINALIZADA){
        navigate(`/meeting/${record.id}/execute`);
        return
      }
      Modal.confirm({
        title: 'Ejecutar reunión',
        content: '¿Seguro que desea ejecutar la reunión?',
        okText: 'Ejecutar',
        cancelText: 'Cancelar',
        async onOk() {
          handleExecuteMeeting(record);
        },
      });
    }
    else if (key === "view"){

      if (record.statusId === MEETING_STATUS.CANCELADA){
        notification.warning({
          message: "No puedes ejecutar una reunión finalizada o cancelada",
          placement: "bottomRight",
          duration: 2,
        });
        return;
      }
      if (record.statusId === MEETING_STATUS.EN_EJECUCION || record.statusId === MEETING_STATUS.FINALIZADA){
        navigate(`/meeting/${record.id}/execute`);
        return
      }
      Modal.confirm({
        title: 'Ejecutar reunión',
        content: '¿Seguro que desea ejecutar la reunión?',
        okText: 'Ejecutar',
        cancelText: 'Cancelar',
        async onOk() {
          handleExecuteMeeting(record);
        },
      });
    }
  };

  const handleExecuteMeeting = async (record) => {
    try{
      await axios.put(`${globals.SERVICE_API_DB}/v1/update-meeting-status-to-execution/${record.id}`,
      null,
      {
        headers: {
          "x-token": globals.token,
        },
      });
    }
    catch(err){
      console.log(err?.response.data);
      let message = "Error ejecutando reunión";
      if (err?.response.data.status === -2){
        message = "No puedes ejecutar la reunión si hay una reunión anterior abierta o en ejecución";
      }
      else if (err?.response.data.status === -4){
        message = "No puedes ejecutar la reunión si hay puntos de reunión sin responsables asignados";
      }
      notification.warning({
        message: message,
        placement: "bottomRight",
        duration: 2,
      });
      return;
    }

    navigate(`/meeting/${record.id}/execute`);
  };

  async function handleTableChange(newPagination) {
    fetchData(newPagination);
  }

  const saveMeetingForm = async ({status, newMeeting}) => {
    setShowMeetingForm(false);
    if (isEdit) {
      setIsEdit(false);
      if (status === "edited") {
        notification.success({
          message: "Reunión editada exitosamente!",
          placement: "bottomRight",
          duration: 2,
        });
      }
      else if (status === "bulk-edited") {
        notification.success({
          message: "Reuniones editadas exitosamente!",
          placement: "bottomRight",
          duration: 2,
        });
        fetchData(null);
      }
      return;
    }
    if(status === "created"){
      setData([...newMeeting, ...data]);
    }

    notification.success({
      message: "Reunión creada exitosamente!",
      placement: "bottomRight",
      duration: 2,
    });
  };

  const closeMeetingForm = () => {
    setShowMeetingForm(false);
    setIsEdit(false);
    setCurrentMeeting(null);
  };

  const deleteMeeting = async (delete_option) => {
    setMessageVisible(false);
    setLoading(true);
    const params = {
      delete_option,
    };
    
    try{
      await axios.put(`${globals.SERVICE_API_DB}/v1/cancel-meeting/${currentMeeting.id}`,
      null,
      {
        headers: {
          "x-token": globals.token,
        },
        params
      });
      setLoading(false);
      fetchData(null);
      notification.success({
        message: "Reunión cancelada exitosamente!",
        placement: "bottomRight",
        duration: 2,
      });
    }
    catch(err){
      setLoading(false);
      console.log(err.response.data);
      if (err.response.data.status === -1){
        notification.error({
          message: "No puedes cancelar estas reuniones, porque algunas ya han ocurrido",
          placement: "bottomRight",
          duration: 2,
        });
      }
      else{
        notification.error({
          message: "Error cancelando reunión",
          placement: "bottomRight",
          duration: 2,
        });
      }
      return;
    }
  };

  const handleRefresh = () => {
    fetchData(null);
  };

  const handleSearch = async () => {
    fetchData(null, searchText, statusId);
  };

  const handleSearchEnterKey = async (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const filterStatusIdOption = (input, option) =>
    (option?.children ?? "").toLowerCase().includes(input.toLowerCase());

  return (
    <div>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
      >
        <h2 style={{ marginRight: "16px" }}>Reuniones</h2>
        <div style={{ flexGrow: 1, borderBottom: "1px solid #ccc" }} />
        <Button
          onClick={() => {
            setIsEdit(false);
            setShowMeetingForm(true);
          }}
          style={{ marginRight: "8px" }}
        >
          Crear
        </Button>
        <Button
          onClick={handleRefresh}
          disabled={loading}
          style={{ marginRight: "8px" }}
        >
          Refrescar
        </Button>
      </div>
      <Input
        placeholder="Buscar"
        allowClear
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16, width: 200 }}
        prefix={<SearchOutlined />}
        onKeyDown={handleSearchEnterKey}
      />
      <Select
        allowClear
        style={{ width: 200, marginLeft: 8 }}
        showSearch
        value={statusId}
        onChange={(value) => setStatusId(value)}
        placeholder="Estados"
        filterOption={filterStatusIdOption}
        optionFilterProp="children"
      >
        {allstatus.map((option) => (
          <Option key={option.id} value={option.id}>
            {option.name}
          </Option>
        ))}
      </Select>
      <Button onClick={handleSearch} style={{ marginRight: "8px" }}>
        Buscar
      </Button>
      <div
        className="site-layout-background h-auto"
        style={{
          padding: 10,
          display: "flex",
          flexDirection: "column",
          margin: 0,
          height: "auto",
          minHeight: "350px",
          overflow: "auto",
          backgroundColor: "#ffffff",
        }}
      >
        <Table
          rowKey={(r) => uuidv4()}
          columns={columns}
          dataSource={data}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ y: "max-content" }}
          size="small"
        />
      </div>
      <LoadingModal open={loading} />

      <MeetingForm
        open={showMeetingForm}
        onFinish={saveMeetingForm}
        onCancel={closeMeetingForm}
        isEdit={isEdit}
        currentMeeting={currentMeeting}
      />

      <DeleteMeetingDialog
        visible={messageVisible}
        title="Está seguro de cancelar esta reunión?"
        message="Esta acción no se puede deshacer."
        onOk={deleteMeeting}
        onCancel={() => setMessageVisible(false)}
        currentMeeting={currentMeeting}
      />
    </div>
  );
};

export default MeetingTable;
